/**
 * D145 — Android's hardware back closes the topmost overlay instead of leaving the screen.
 *
 * The shell routes on the History API (`Router.tsx`), and Android's back button reaches a web app
 * only as `popstate`. So an overlay that pushes no entry is invisible to it: pressing back while
 * a drawer, sheet or dialog was open navigated away from the screen *underneath* it, leaving the
 * overlay's own state to be discarded on the way out. Every overlay relied on a backdrop tap or
 * an explicit Cancel — and no overlay in this app handles `Escape` either, so on a phone the back
 * button was the one dismissal gesture the platform offers and the one the app did not answer.
 *
 * **This hook does not own whether the overlay is open.** The caller keeps its own `open` state,
 * because that state is entangled with things a router has no business holding: the member sheet's
 * selected row, the rename popover's typed value, a dialog's in-flight and error flags. The hook
 * keeps that state and a history entry in step, in both directions:
 *
 *  - `open` goes true  → push an entry.
 *  - the entry is popped (back button) → `onClose()` fires.
 *  - `open` goes false (backdrop, Cancel) → the entry is consumed via `history.go`, so a
 *    programmatic close and a back press take the same path and neither leaves a spent entry.
 *
 * **`ownsEntry` is a ref, not state, and it is what makes the two directions non-reentrant.** A
 * back press clears it *before* calling `onClose()`, so the caller's own `setOpen(false)` finds
 * nothing left to unwind; a Cancel clears it before `close()`, so the truncation that follows
 * reports nothing to close. Without it each dismissal would drive the other one.
 *
 * **No unmount cleanup, deliberately.** `main.tsx` renders under `StrictMode`, which mounts,
 * unmounts and remounts every effect in development — a cleanup that called `history.go` would
 * consume a real entry on a simulated unmount. The entry is reclaimed by the router instead:
 * `commit()` unwinds open overlays before it navigates, and the auth-guard redirect clears them.
 * The effect below is idempotent for the same reason, so StrictMode's second pass is a no-op.
 */
import { useEffect, useRef } from 'react'
import { useOverlayHistory } from './Router'

export function useDismissOnBack(
  /** Stable, unique per overlay. Two overlays sharing a key would share one history entry. */
  key: string,
  open: boolean,
  onClose: () => void,
  options: {
    /**
     * `false` refuses the back button exactly as `DeleteGateDialog` already refuses its backdrop
     * while a write is in flight — cancelling the dialog would not cancel the request, so the
     * screen would be lying about a destructive operation that is still running.
     */
    readonly dismissible?: boolean
  } = {},
): void {
  const overlayHistory = useOverlayHistory()
  const dismissible = options.dismissible ?? true

  // Read through refs so neither identity is an effect dependency: `onClose` is almost always a
  // fresh closure per render, and re-running the push/pop effect for that would open and close
  // the overlay on every keystroke in it.
  const onCloseRef = useRef(onClose)
  onCloseRef.current = onClose
  const historyRef = useRef(overlayHistory)
  historyRef.current = overlayHistory
  const ownsEntry = useRef(false)

  useEffect(() => {
    if (open && !ownsEntry.current) {
      ownsEntry.current = true
      historyRef.current.open(key, dismissible)
      return
    }
    if (!open && ownsEntry.current) {
      ownsEntry.current = false
      historyRef.current.close(key)
    }
    // `dismissible` is read on open but is not a dependency — the effect below owns its later
    // changes, so a dialog going busy updates the registration rather than re-pushing an entry.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, key])

  useEffect(() => {
    if (ownsEntry.current) historyRef.current.setDismissible(key, dismissible)
  }, [dismissible, key])

  const { openKeys } = overlayHistory
  useEffect(() => {
    if (ownsEntry.current && !openKeys.includes(key)) {
      ownsEntry.current = false
      onCloseRef.current()
    }
  }, [openKeys, key])
}
